import { useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { initLogger, logActivity, processQueue } from '../lib/logger';

/**
 * Hook to initialize the logger with Clerk authentication
 * Tracks user sessions, sign-ins, and activity
 */
export function useLoggerInit() {
  const { getToken, isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const hasLoggedSignIn = useRef(false);
  const previousSignedIn = useRef<boolean | null>(null);

  // Initialize logger with Clerk's token getter
  useEffect(() => {
    initLogger(async () => {
      try {
        const token = await getToken();
        return token;
      } catch {
        return null;
      }
    });

    // Process any queued logs on init
    processQueue();
  }, [getToken]);

  // Track sign-in/sign-out events
  useEffect(() => {
    // Skip on initial load
    if (previousSignedIn.current === null) {
      previousSignedIn.current = isSignedIn ?? false;
      
      // Log initial session if signed in
      if (isSignedIn && userId && !hasLoggedSignIn.current) {
        hasLoggedSignIn.current = true;
        logActivity('user_signed_in', { 
          userId,
          email: user?.primaryEmailAddress?.emailAddress,
          timestamp: new Date().toISOString(),
        });
      }
      return;
    }

    // Detect sign-in
    if (isSignedIn && !previousSignedIn.current && userId) {
      hasLoggedSignIn.current = true;
      logActivity('user_signed_in', { 
        userId,
        email: user?.primaryEmailAddress?.emailAddress,
        timestamp: new Date().toISOString(),
      });
    }

    // Detect sign-out
    if (!isSignedIn && previousSignedIn.current) {
      hasLoggedSignIn.current = false;
      logActivity('user_signed_out', { 
        timestamp: new Date().toISOString(),
      });
    }

    previousSignedIn.current = isSignedIn ?? false;
  }, [isSignedIn, userId, user]);

  // Periodic activity heartbeat (every 5 minutes while active)
  useEffect(() => {
    if (!isSignedIn || !userId) return;

    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        logActivity('user_active', { 
          userId,
          timestamp: new Date().toISOString(),
        });
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(heartbeatInterval);
  }, [isSignedIn, userId]);

  // Track page visibility changes
  useEffect(() => {
    if (!isSignedIn || !userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logActivity('app_resumed', { 
          userId,
          timestamp: new Date().toISOString(),
        });
      } else {
        logActivity('app_backgrounded', { 
          userId,
          timestamp: new Date().toISOString(),
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isSignedIn, userId]);
}

/**
 * Hook to track page views
 * Use this in individual page components
 */
export function usePageView(pageName: string) {
  const { userId } = useAuth();
  const hasLogged = useRef(false);

  useEffect(() => {
    if (!hasLogged.current) {
      hasLogged.current = true;
      logActivity('page_view', { 
        page: pageName,
        userId: userId || 'anonymous',
        timestamp: new Date().toISOString(),
      });
    }
  }, [pageName, userId]);
}
